/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { MutableRefObject } from "react";
import { useFormStoreFieldValue, useFormStoreSetters } from "../FormStore";
import { cn } from "@/shared/ui/utils/tailwind";

export type RadioProps = {
  name?: string;
  selected?: boolean;
  value: any;
  radioRef?: MutableRefObject<HTMLElement | undefined>;
};

type RadioGroupContextValue = {
  value: any;
  onChange: (value: any) => void;
};

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(null);

type RadioComponent = React.FC<RadioProps> & { Group: React.FC<RadioGroupProps> };

/**
 * Radio button input. Reads its selected value from an enclosing
 * `Radio.Group` when present, otherwise falls back to the form store.
 *
 * @param {RadioProps} props - Radio properties.
 * @returns {JSX.Element} Rendered radio.
 */
const RadioBase: React.FC<RadioProps> = ({
  name,
  value,
  radioRef,
}) => {
  const group = React.useContext(RadioGroupContext);
  const { setFieldValue } = useFormStoreSetters();
  const storeValue = useFormStoreFieldValue(name ?? '');
  const selectedValue = group ? group.value : storeValue;
  const isSelected = value === selectedValue;

  const select = (event: React.MouseEvent) => {
    event.preventDefault();
    if (group) {
      group.onChange(value);
    } else if (name) {
      setFieldValue(name, value);
    }
  };

  return (
    <button
      type="button"
      ref={radioRef as any}
      className={cn(
        "flex items-center justify-center rounded-full border-[3px] border-[var(--border-medium)] w-5 h-5",
        "transition-all hover:shadow-[0_0_0_3px_var(--border-subtle)] hover:cursor-pointer",
        "focus:border-[var(--seed-base)] focus:outline-none",
        isSelected && "border-[var(--seed-base)] animate-pop"
      )}
      onClick={select}
    >
      {isSelected && (
        <div className="rounded-full bg-[var(--seed-base)] h-[calc(100%-5px)] w-[calc(100%-5px)]" />
      )}
    </button>
  );
};

export type RadioGroupProps = {
  value: any;
  onChange: (value: any) => void;
  children?: React.ReactNode;
};

/**
 * Groups radio buttons and manages the selected value via context.
 *
 * @param {RadioGroupProps} props - Group properties.
 * @returns {JSX.Element} Rendered group.
 */
const RadioGroup: React.FC<RadioGroupProps> = ({ value, onChange, children }) => (
  <RadioGroupContext.Provider value={{ value, onChange }}>
    {children}
  </RadioGroupContext.Provider>
);

const Radio = RadioBase as RadioComponent;
Radio.Group = RadioGroup;

export default Radio;
